import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, Briefcase, Plus, RotateCcw, XCircle } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import StatusBadge from '../../components/StatusBadge';
import StatusFilter, { LifecycleStatusFilter } from '../../components/StatusFilter';
import ListPager from '../../components/ListPager';
import AdminListToolbar from '../../components/AdminListToolbar';
import MultiSelectFilter from '../../components/MultiSelectFilter';
import BulkActionsBar, { bulkResultMessage } from '../../components/BulkActionsBar';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { useRowSelection } from '../../hooks/useRowSelection';
import { formatDate } from '../../lib/format';
import { searchActiveOrganizations } from '../../lib/pickers';

interface AdminWorkspaceRow {
  id: string;
  name: string;
  description: string | null;
  owner_org_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  organization_name: string | null;
  organization_subscription: string | null;
  organization_is_active: boolean | null;
  bill_count: string;
  member_count: string;
}

const parseWorkspaces = (raw: unknown): AdminListResult<AdminWorkspaceRow> => {
  const data = raw as {
    workspaces?: AdminWorkspaceRow[];
    pagination?: { total?: number; limit?: number; offset?: number };
  };
  const total = data.pagination?.total ?? 0;
  const limit = data.pagination?.limit ?? 0;
  const offset = data.pagination?.offset ?? 0;
  return {
    rows: data.workspaces ?? [],
    total,
    hasMore: offset + limit < total,
  };
};

interface ActiveOrgOption {
  id: string;
  name: string;
}

const COLUMN_COUNT = 9;

const WorkspacesView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<LifecycleStatusFilter>('active');
  const [orgFilterIds, setOrgFilterIds] = useState<string[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', ownerOrgId: '' });
  const [deactivateTarget, setDeactivateTarget] = useState<AdminWorkspaceRow | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<AdminWorkspaceRow | null>(null);
  const selection = useRowSelection();
  const [bulkAction, setBulkAction] = useState<'deactivate' | 'reactivate' | null>(null);

  const list = useAdminList<AdminWorkspaceRow>({
    queryKey: 'accounts-workspaces',
    endpoint: '/admin/workspaces',
    parse: parseWorkspaces,
    filters: {
      status,
      organization_ids: orgFilterIds.length > 0 ? orgFilterIds.join(',') : undefined,
    },
    initialSort: { key: 'updated_at', direction: 'desc' },
  });

  // Active organizations — feeds both the toolbar filter and the create form's owner select
  const orgOptions = useQuery({
    queryKey: ['admin-accounts', 'active-org-options'],
    queryFn: async () => {
      const response = await api.get('/admin/accounts/organizations', {
        params: { status: 'active', limit: 100 },
      });
      return (response.data.data.organizations as ActiveOrgOption[]).map(({ id, name }) => ({ id, name }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-list'] });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/accounts/workspaces', {
        name: form.name,
        description: form.description || null,
        owner_org_id: form.ownerOrgId,
      }),
    onSuccess: () => {
      toast.success('Workspace created');
      setCreateOpen(false);
      setForm({ name: '', description: '', ownerOrgId: '' });
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to create workspace');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/workspaces/${id}/deactivate`),
    onSuccess: () => {
      toast.success('Workspace deactivated');
      setDeactivateTarget(null);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to deactivate workspace');
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.put(`/admin/workspaces/${id}/reactivate`),
    onSuccess: () => {
      toast.success('Workspace reactivated');
      setReactivateTarget(null);
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Failed to reactivate workspace');
    },
  });

  const canSubmitCreate = form.name.trim().length > 0 && form.ownerOrgId !== '';

  const bulkLifecycleMutation = useMutation({
    mutationFn: (action: 'deactivate' | 'reactivate') =>
      api.post('/admin/accounts/workspaces/bulk-lifecycle', { workspace_ids: selection.ids, action }),
    onSuccess: (response, action) => {
      toast.success(bulkResultMessage(`${action}d`, response.data.data));
      setBulkAction(null);
      selection.clear();
      invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error || 'Bulk operation failed');
    },
  });

  const pageIds = list.rows.map((workspace) => workspace.id);

  return (
    <div className="space-y-4">
      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search workspaces or organizations…"
        searchAriaLabel="Search workspaces"
        actions={
          <Button variant="primary" size="sm" icon={Plus} onClick={() => setCreateOpen(true)}>
            Create Workspace
          </Button>
        }
      >
        <StatusFilter value={status} onChange={setStatus} />
        <MultiSelectFilter
          label="Organizations"
          fetchOptions={searchActiveOrganizations}
          selectedIds={orgFilterIds}
          onChange={setOrgFilterIds}
        />
      </AdminListToolbar>

      <BulkActionsBar count={selection.count} noun="workspace" onClear={selection.clear}>
        <Button variant="outline" size="sm" icon={XCircle} onClick={() => setBulkAction('deactivate')}>
          Deactivate
        </Button>
        <Button variant="outline" size="sm" icon={RotateCcw} onClick={() => setBulkAction('reactivate')}>
          Reactivate
        </Button>
      </BulkActionsBar>

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load workspaces. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell isCheckbox>
                <input
                  type="checkbox"
                  aria-label="Select all workspaces on this page"
                  checked={selection.allSelected(pageIds)}
                  onChange={(event) => selection.setPage(pageIds, event.target.checked)}
                />
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="md" sortable sortKey="organization_name" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Organization
              </StandardTableHeaderCell>
              <StandardTableHeaderCell align="right" columnType="fit" sortable sortKey="member_count" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Members
              </StandardTableHeaderCell>
              <StandardTableHeaderCell align="right" columnType="fit" responsiveHide="lg" sortable sortKey="bill_count" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Bills
              </StandardTableHeaderCell>
              <StandardTableHeaderCell sortable sortKey="is_active" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Status
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="lg" columnType="fit" sortable sortKey="updated_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Updated
              </StandardTableHeaderCell>
              <StandardTableHeaderCell responsiveHide="xl" columnType="fit" sortable sortKey="created_at" currentSortKey={list.sortKey} sortDirection={list.sortDirection} onSort={list.handleSort}>
                Created
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="actions">Actions</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={list.search ? `No workspaces match "${list.search}"` : 'No workspaces found'}
                icon={<Briefcase className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((workspace) => (
                <StandardTableRow
                  key={workspace.id}
                  selected={selection.has(workspace.id)}
                  onClick={() => navigate(`/admin-next/accounts/workspaces/${workspace.id}`)}
                >
                  <StandardTableCell isCheckbox>
                    <div onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${workspace.name}`}
                        checked={selection.has(workspace.id)}
                        onChange={() => selection.toggle(workspace.id)}
                      />
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>
                    <span className="font-medium text-text-primary dark:text-text-dark-primary">
                      {workspace.name}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="md">
                    {workspace.organization_name ?? '—'}
                  </StandardTableCell>
                  <StandardTableCell align="right">{workspace.member_count}</StandardTableCell>
                  <StandardTableCell align="right" responsiveHide="lg">{workspace.bill_count}</StandardTableCell>
                  <StandardTableCell>
                    <StatusBadge
                      active={workspace.is_active}
                      viaParent={workspace.is_active && workspace.organization_is_active === false}
                    />
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">{formatDate(workspace.updated_at)}</StandardTableCell>
                  <StandardTableCell responsiveHide="xl">{formatDate(workspace.created_at)}</StandardTableCell>
                  <StandardTableCell>
                    <div onClick={(event) => event.stopPropagation()}>
                      {workspace.is_active ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={XCircle}
                          onClick={() => setDeactivateTarget(workspace)}
                          ariaLabel={`Deactivate ${workspace.name}`}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={RotateCcw}
                          onClick={() => setReactivateTarget(workspace)}
                          ariaLabel={`Reactivate ${workspace.name}`}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      {!list.isError && (
        <ListPager
          offset={list.offset}
          limit={list.limit}
          total={list.total}
          page={list.page}
          pageCount={list.pageCount}
          hasPrev={list.hasPrev}
          hasNext={list.hasNext}
          onPrev={list.prevPage}
          onNext={list.nextPage}
          noun="workspaces"
        />
      )}

      <AdminModal
        isOpen={createOpen}
        title="Create Workspace"
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!canSubmitCreate}
              loading={createMutation.isLoading}
              onClick={() => createMutation.mutate()}
            >
              Create Workspace
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="ws-name" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Name *
            </label>
            <input
              id="ws-name"
              type="text"
              value={form.name}
              maxLength={255}
              onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            />
          </div>
          <div>
            <label htmlFor="ws-org" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Owning organization *
            </label>
            <select
              id="ws-org"
              value={form.ownerOrgId}
              onChange={(event) => setForm((current) => ({ ...current, ownerOrgId: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            >
              <option value="">Select an active organization…</option>
              {(orgOptions.data ?? []).map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
            {orgOptions.isLoading && (
              <p className="mt-1 text-xs text-text-tertiary dark:text-text-dark-tertiary">Loading organizations…</p>
            )}
          </div>
          <div>
            <label htmlFor="ws-description" className="mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
              Description
            </label>
            <textarea
              id="ws-description"
              value={form.description}
              rows={3}
              maxLength={2000}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              className="w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary"
            />
          </div>
        </div>
      </AdminModal>

      <ConfirmationDialog
        isOpen={deactivateTarget !== null}
        title="Deactivate workspace"
        message={
          deactivateTarget
            ? `Deactivate "${deactivateTarget.name}"? Members lose access until it is reactivated. Nothing is deleted.`
            : ''
        }
        confirmText="Deactivate"
        variant="warning"
        onConfirm={() => deactivateTarget && deactivateMutation.mutate(deactivateTarget.id)}
        onCancel={() => setDeactivateTarget(null)}
      />

      <ConfirmationDialog
        isOpen={reactivateTarget !== null}
        title="Reactivate workspace"
        message={
          reactivateTarget
            ? `Reactivate "${reactivateTarget.name}"? Members regain access with their previous roles.`
            : ''
        }
        confirmText="Reactivate"
        variant="info"
        onConfirm={() => reactivateTarget && reactivateMutation.mutate(reactivateTarget.id)}
        onCancel={() => setReactivateTarget(null)}
      />

      <ConfirmationDialog
        isOpen={bulkAction !== null}
        title={bulkAction === 'deactivate' ? 'Deactivate selected workspaces' : 'Reactivate selected workspaces'}
        message={
          bulkAction === 'deactivate'
            ? `Deactivate ${selection.count} selected workspace${selection.count === 1 ? '' : 's'}? Members lose access until they are reactivated. Already-deactivated workspaces are skipped. Nothing is deleted.`
            : `Reactivate ${selection.count} selected workspace${selection.count === 1 ? '' : 's'}? Members regain access with their previous roles. Already-active workspaces are skipped.`
        }
        confirmText={bulkAction === 'deactivate' ? 'Deactivate All' : 'Reactivate All'}
        variant={bulkAction === 'deactivate' ? 'warning' : 'info'}
        onConfirm={() => bulkAction && bulkLifecycleMutation.mutate(bulkAction)}
        onCancel={() => setBulkAction(null)}
      />
    </div>
  );
};

export default WorkspacesView;
